import React from "react";
import { observer } from "mobx-react-lite";
import { EditorContext, useEditor } from "@tiptap/react";
import { OnSuccessContext } from "@tiptap-pro/extension-ai";
import { watchPreviewContent } from "@tiptap-pro/extension-snapshot";
import isEqual from "lodash/isEqual";
import { useParams } from "react-router-dom";

import { TocProvider, useToc } from "containers/Tiptap/components/tiptap-node/toc-node/context/toc-context";

import { useEditorStore } from "containers/Tiptap/hooks/useEditorStore";

import { useStore } from "utils/hooks/store";
import API from "utils/api";
import { useWindowSize } from "utils/hooks/useWindowSize";
import TiptapTextSelectionToolbar from "components/TextSelectionToolbar/TiptapTextSelectionToolbar";
import { createEditorConfig } from "Tiptap/editor-config";
import { useReadOnlyPagesLayout } from "containers/Tiptap/hooks/use-read-only-pages-layout";
import { useReadOnlyPreviewEditor } from "containers/Tiptap/hooks/use-read-only-preview-editor";
import { revertBodyAndSettings } from "containers/Tiptap/lib/version-preview";

import { EditorLoadingStates } from "Tiptap/components/tiptap-templates/simple-editor/EditorLoadingStates";

import { EditorToolbar } from "Tiptap/components/tiptap-templates/simple-editor/EditorToolbar";

import { EditorContentWrapper } from "Tiptap/components/tiptap-templates/simple-editor/EditorContent";

import { EditorSidebar } from "Tiptap/components/tiptap-templates/simple-editor/EditorSidebar";
import { EditorModals } from "Tiptap/components/tiptap-templates/simple-editor";
import { VersionHistoryModal } from "containers/Tiptap/components/tiptap-ui/VersioningContainer";
import { DocumentHeader } from "containers/DocumentsDetails/DocumentHeader/DocumentHeader";

import { DocumentAccessLevel } from "utils/api/modules/entities/entities.types";
import { EDITOR_STATUS, SidebarEnum } from "./types";
import { useCollaborationProvider } from "./hooks/useCollaborationProvider";
import { useThreads } from "./hooks/useThreads";
import { cleanupEmptyTables } from "./utils";
import { ReadOnlyEditorContent } from "./styles";
import { FileUploadProvider } from "../tiptap-node/image-upload-node/image-upload-node";

import "./extension-styles.css";
import "./simple-editor.scss";
import { Box, Alert } from "@mui/material";

// oxlint-disable-next-line typescript/no-explicit-any
const normalizeNode = (node: any): any => {
  if (!node || typeof node !== "object") return node;

  const { attrs, content, ...rest } = node;

  const normalized: Record<string, unknown> = { ...rest };

  if (attrs) {
    const { textAlign, id, "data-toc-id": _tocId, ...otherAttrs } = attrs;
    const cleanAttrs: Record<string, unknown> = { ...otherAttrs };
    if (textAlign != null) {
      cleanAttrs.textAlign = textAlign;
    }
    if (Object.keys(cleanAttrs).length > 0) {
      normalized.attrs = cleanAttrs;
    }
  }

  if (content && Array.isArray(content) && content.length > 0) {
    normalized.content = content.map(normalizeNode);
  }

  return normalized;
};

// oxlint-disable-next-line typescript/no-explicit-any
const isContentEqual = (a: any, b: any): boolean => isEqual(normalizeNode(a), normalizeNode(b));

interface SimpleEditorProps {
  tiptapToken?: string;
  organisation?: string;
  documentName: string;
}

const SimpleEditorContent = observer(
  ({ tiptapToken, organisation, documentName }: SimpleEditorProps): React.ReactNode => {
    const params = useParams<{ entityType: string; entityId: string; shareId: string }>();

    const { session, notification, documentsDetails } = useStore();
    const { width: windowWidth } = useWindowSize();
    const editorStore = useEditorStore();
    const { setTocContent } = useToc();

    // Use per-document token if available, fall back to session-level token
    const effectiveToken = documentsDetails.documentToken || tiptapToken;

    const getTiptapToken = React.useCallback(async (): Promise<string> => {
      if (documentsDetails.documentToken) {
        await documentsDetails.loadDocumentToken(params.entityId!);
        return documentsDetails.documentToken || '';
      }
      const { data } = await API.entities.loadSharedDocument(
        params.entityType!,
        params.entityId!,
        params.shareId!
      )();
      return data.tiptapToken;
    }, [documentsDetails, params.entityId, params.entityType, params.shareId]);

    // View-only if shared link OR if user only has view access
    const isViewOnly = Boolean(params.shareId) || documentsDetails.accessLevel === DocumentAccessLevel.View;
    const editable = !isViewOnly;

    const { ydoc, status, provider } = useCollaborationProvider({
      uuid: params.entityId!,
      session,
      tiptapToken: effectiveToken,
      getTiptapToken,
      organisation,
      editable,
    });

    // previewEditor displays the published snapshot version in shared document view
    // Rebuilt once the ydoc has synced so the schema picks up the stored view mode (Pages).
    const {
      editor: previewEditor,
      contentApplied: previewApplied,
      markContentApplied: markPreviewApplied,
    } = useReadOnlyPreviewEditor(setTocContent, ydoc, [status === EDITOR_STATUS.SYNCED]);

    // oxlint-disable-next-line typescript/no-explicit-any
    const [previewContent, setPreviewContent] = React.useState<any>(null);

    const cleanupTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const editor = useEditor({
      editable,
      immediatelyRender: false,
      shouldRerenderOnTransaction: true,
      extensions: React.useMemo(
        () =>
          createEditorConfig({
            provider,
            ydoc,
            entityId: params.entityId!,
            editable,
            aiConfig: {
              token: session.user?.tiptapAiUserToken || "",
              onLoading: () => {
                editorStore.setAILoading();
              },
              onChunk: ({ response }) => {
                editorStore.setAIChunk(response);

                if (editor) {
                  cleanupEmptyTables(editor);
                }
              },
              onSuccess: (context: OnSuccessContext) => {
                const finalResponse = context.response || "";

                editorStore.setAISuccess(finalResponse);
                context.editor.commands.setShowAiChanges(true);

                cleanupTimeoutRef.current = setTimeout(() => {
                  if (context.editor && !context.editor.isDestroyed) {
                    cleanupEmptyTables(context.editor);
                  }
                  cleanupTimeoutRef.current = null;
                }, 300);
              },
              onError: (error, context) => {
                editorStore.setAIError(error.message);
                context.editor.commands.stopTrackingAiChanges();
              },
            },
            onSnapshotUpdate: editorStore.setSnapshotState,
            onClickThread: (id: string | null) => {
              const isResolved = threadsRef.current.find((t) => t.id === id)?.resolvedAt;

              if (!id || isResolved) {
                setSelectedThread(null);
                editor?.chain().unselectThread().run();
                return;
              }

              setSelectedThread(id);
              editorStore.setActiveSidebar(SidebarEnum.Comments);
              editor?.chain().selectThread({ id, updateSelection: false }).run();
            },
            onMediaError: notification.enqueueErrorSnackbar,
          }),
        [params.entityId, editable, provider, ydoc, session.user?.tiptapAiUserToken, session.user?.tiptapConvertToken, editorStore],
      ),
    });

    const {
      threads,
      selectedThread,
      setSelectedThread,
      selectThreadInEditor,
      deleteThread,
      resolveThread,
      unresolveThread,
      updateComment,
      onHoverThread,
      onLeaveThread,
    } = useThreads(provider, editor);

    const threadsRef = React.useRef(threads);

    React.useEffect(() => {
      threadsRef.current = threads;
    }, [threads]);

    React.useEffect(
      () => () => {
        if (cleanupTimeoutRef.current) {
          clearTimeout(cleanupTimeoutRef.current);
        }
        if (pendingVersionTimeoutRef.current) {
          clearTimeout(pendingVersionTimeoutRef.current);
        }
      },
      [],
    );

    // Disable auto-versioning once connected (we use manual versioning only)
    const autoVersioningDisabledRef = React.useRef(false);

    React.useEffect(() => {
      if (
        !editable ||
        status !== EDITOR_STATUS.SYNCED ||
        !editor ||
        autoVersioningDisabledRef.current ||
        !editorStore.snapshotState.autoVersioning
      ) {
        return undefined;
      }

      const disableAutoVersioning = ({
        transaction,
      }: {
        transaction: { getMeta: (key: string) => unknown };
      }): void => {
        if (autoVersioningDisabledRef.current || transaction.getMeta("y-sync$")) return;
        editor.commands.toggleVersioning();
        autoVersioningDisabledRef.current = true;
      };

      editor.on("update", disableAutoVersioning);
      return () => {
        editor.off("update", disableAutoVersioning);
      };
    }, [editable, status, editor, editorStore.snapshotState.autoVersioning]);

    React.useEffect(() => {
      if (
        editable !== false ||
        !provider ||
        status !== EDITOR_STATUS.SYNCED ||
        !editorStore.snapshotReady ||
        editorStore.versions.length === 0
      ) {
        return undefined;
      }

      const latestVersion = editorStore.versions.at(-1)!.version;

      provider.sendStateless(
        JSON.stringify({ action: "version.preview", version: latestVersion }),
      );

      const unbind = watchPreviewContent(provider, (content) => {
        setPreviewContent(content);
        unbind();
      });

      return () => {
        unbind();
      };
    }, [editable, provider, status, editorStore.snapshotReady, editorStore.versions.length]);

    // Apply preview content to previewEditor once both are ready.
    // setTimeout defers setContent out of React's commit phase — Tiptap's ReactRenderer
    // calls flushSync internally (to synchronously mount node views), which is illegal
    // while React is still committing.
    React.useEffect(() => {
      if (!previewContent || !previewEditor) return undefined;

      const id = setTimeout(() => {
        if (!previewEditor.isDestroyed) {
          previewEditor.commands.setContent(previewContent);
          markPreviewApplied();
        }
      }, 0);

      return () => clearTimeout(id);
    }, [previewContent, previewEditor, markPreviewApplied]);

    useReadOnlyPagesLayout({
      editor: previewEditor,
      ydoc,
      contentApplied: previewApplied,
      variables: { documentName, documentVersion: String(editorStore.versions.at(-1)?.version ?? '') },
      shareId: params.shareId,
    });

    // Track content changes during editing session
    const hasCheckedUnpublishedChanges = React.useRef(false);

    // After snapshot state is ready, detect unpublished changes by comparing current content with latest version
    React.useEffect(() => {
      if (
        !editorStore.snapshotReady ||
        hasCheckedUnpublishedChanges.current ||
        !editor ||
        !provider ||
        status !== EDITOR_STATUS.SYNCED
      ) {
        return undefined;
      }

      const { versions } = editorStore;

      if (versions.length === 0) {
        // No versions ever published — any non-empty content means unpublished changes
        const content = editor.getJSON();
        const hasContent = content.content?.some(
          (node) => node.type !== "paragraph" || (node.content && node.content.length > 0),
        );

        hasCheckedUnpublishedChanges.current = true;

        if (hasContent) {
          editorStore.setHasUnpublishedChanges(true);
        }
        return undefined;
      }

      hasCheckedUnpublishedChanges.current = true;

      // Compare current content with latest published version
      const latestVersion = versions[versions.length - 1];

      provider.sendStateless(
        JSON.stringify({
          action: "version.preview",
          version: latestVersion.version,
        }),
      );

      const unbind = watchPreviewContent(provider, (content) => {
        if (!isContentEqual(editor.getJSON(), content)) {
          editorStore.setHasUnpublishedChanges(true);
        }

        unbind();
      });

      return () => {
        unbind();
      };
    }, [editorStore.snapshotReady, editor, provider, status]);

    React.useEffect(() => {
      if (!editor) return undefined;

      const handleUpdate = ({
        transaction,
      }: {
        transaction: { getMeta: (key: string) => unknown };
      }): void => {
        if (
          !editorStore.snapshotReady ||
          !hasCheckedUnpublishedChanges.current ||
          transaction.getMeta("y-sync$")
        )
          return;
        editorStore.setHasUnpublishedChanges(true);
      };

      editor.on("update", handleUpdate);
      return () => {
        editor.off("update", handleUpdate);
      };
    }, [editor, editorStore]);

    const handleOpenSaveVersionModal = React.useCallback(() => {
      if (!editor || editorStore.isPublishing) return;

      if (status !== EDITOR_STATUS.SYNCED && status !== EDITOR_STATUS.CONNECTED) {
        notification.enqueueErrorSnackbar(
          "Cannot save version - editor is not connected. Please wait and try again.",
        );
        return;
      }

      const nextVersionNumber = editorStore.versions.length + 1;
      editorStore.openPublishModal(nextVersionNumber);
    }, [editor, editorStore, notification, status]);

    // Track pending version save info for success notification
    const pendingVersionRef = React.useRef<{ versionNumber: number } | null>(null);
    const pendingVersionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Watch for version count changes to confirm save success
    const previousVersionCountRef = React.useRef(editorStore.versions.length);

    React.useEffect(() => {
      const currentCount = editorStore.versions.length;

      if (currentCount > previousVersionCountRef.current) {
        const versions = editorStore.versions;
        if (versions.length > 0 && params.entityId && !isViewOnly) {
          const latestVersion = Math.max(...versions.map((v) => v.version));
          API.entities
            .setDocumentTiptapVersion(params.entityId, latestVersion)()
            .catch(() => {
              notification.enqueueErrorSnackbar(
                "Could not sync document version — acknowledgement requests on this document may use a stale version. Re-publish to retry.",
              );
            });
        }

        if (pendingVersionRef.current) {
          if (pendingVersionTimeoutRef.current) {
            clearTimeout(pendingVersionTimeoutRef.current);
            pendingVersionTimeoutRef.current = null;
          }
          const { versionNumber } = pendingVersionRef.current;
          pendingVersionRef.current = null;
          editorStore.setHasUnpublishedChanges(false);
          editorStore.closePublishModal();
          editorStore.setIsPublishing(false);
          notification.enqueueSuccessSnackbar(`Version ${versionNumber} published`);
        }
      }

      previousVersionCountRef.current = currentCount;
    }, [editorStore.versions.length, editorStore, notification, params.entityId, isViewOnly]);

    const handleSaveVersion = React.useCallback(
      (versionName: string) => {
        if (!editor || editorStore.isPublishing) return;

        if (status !== EDITOR_STATUS.SYNCED && status !== EDITOR_STATUS.CONNECTED) {
          notification.enqueueErrorSnackbar(
            "Cannot save version - editor is not connected. Please wait and try again.",
          );
          editorStore.closePublishModal();
          return;
        }

        editorStore.setIsPublishing(true);

        try {
          const success = editor.commands.saveVersion(versionName);

          if (!success) {
            notification.enqueueErrorSnackbar("Failed to save version. Please try again.");
            editorStore.setIsPublishing(false);
            return;
          }

          pendingVersionRef.current = { versionNumber: editorStore.publishVersionNumber };

          pendingVersionTimeoutRef.current = setTimeout(() => {
            if (pendingVersionRef.current) {
              pendingVersionRef.current = null;
              editorStore.setIsPublishing(false);
              editorStore.closePublishModal();
              editorStore.setHasUnpublishedChanges(false);
              notification.enqueueErrorSnackbar(
                "No changes to publish. Your content matches the latest version.",
              );
            }
          }, 5000);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
          notification.enqueueErrorSnackbar(`Failed to save version: ${errorMessage}`);
          editorStore.setIsPublishing(false);
        }
      },
      [editor, editorStore, notification, status],
    );

    const handleRevertVersion = (version: number): void => {
      const versionData = editorStore.versions.find((v) => v.version === version);
      revertBodyAndSettings(provider, version, versionData?.name || `Version ${version}`);
    };

    if (!editor) {
      return <EditorLoadingStates type="editorError" />;
    }

    if (status === EDITOR_STATUS.ERROR) {
      return <EditorLoadingStates type="error" />;
    }

    if (status === EDITOR_STATUS.DISCONNECTED || !editor) {
      return <EditorLoadingStates type="loading" />;
    }

    if (editable === false) {
      if (!editorStore.snapshotReady) {
        return <EditorLoadingStates type="loading" />;
      }
      if (editorStore.versions.length === 0) {
        return (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
            This document has not been published yet.
          </Box>
        );
      }
      if (!previewContent || !previewEditor) {
        return <EditorLoadingStates type="loading" />;
      }
      return <ReadOnlyEditorContent editor={previewEditor} className="simple-editor-content" />;
    }

    return (
      <Box sx={{ margin: "16px auto", width: "80%" }}>
        <EditorContext.Provider value={{ editor }}>
          <FileUploadProvider
            entityId={params.entityId!}
            enqueueErrorSnackbar={notification.enqueueErrorSnackbar}
          >
            <EditorToolbar
              activeSidebar={editorStore.activeSidebar}
              setActiveSidebar={editorStore.setActiveSidebar}
              setShareModalOpen={editorStore.setShareModalOpen}
              errorMessage={editorStore.aiState.errorMessage}
              hasUnpublishedChanges={editorStore.hasUnpublishedChanges}
              isPublishing={editorStore.isPublishing}
              onPublish={editable ? handleOpenSaveVersionModal : undefined}
              canShare={documentsDetails.accessLevel !== DocumentAccessLevel.View}
            />

            {isViewOnly && !params.shareId && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You have view-only access to this document. Contact the owner to request edit permissions.
              </Alert>
            )}

            <DocumentHeader />

            <EditorContentWrapper
              editor={editor}
              threads={threads}
              selectedThread={selectedThread}
              selectedThreads={editor.storage.comments.focusedThreads}
              activeSidebar={editorStore.activeSidebar}
              windowWidth={windowWidth}
              onDeleteThread={deleteThread}
              onHoverThread={onHoverThread}
              onLeaveThread={onLeaveThread}
              onResolveThread={resolveThread}
              onUnresolveThread={unresolveThread}
              onUpdateComment={updateComment}
              onClickThread={selectThreadInEditor}
              setSelectedThread={setSelectedThread}
            >
              <EditorSidebar
                provider={provider}
                threads={threads}
                activeSidebar={editorStore.activeSidebar}
                setActiveSidebar={editorStore.setActiveSidebar}
                setSelectedThread={setSelectedThread}
              />
            </EditorContentWrapper>

            <TiptapTextSelectionToolbar editor={editor} />

            <EditorModals
              shareModalOpen={editorStore.shareModalOpen}
              entityId={params.entityId!}
              onCloseShareModal={editorStore.closeShareModal}
              hasPublishedVersion={editorStore.versions.length > 0}
              saveVersionModalOpen={editorStore.publishModalOpen}
              onCloseSaveVersionModal={editorStore.closePublishModal}
              onConfirmSaveVersion={handleSaveVersion}
              isSavingVersion={editorStore.isPublishing}
              nextVersionNumber={editorStore.publishVersionNumber}
            />

            <VersionHistoryModal
              provider={provider}
              ydoc={ydoc}
              viewMode={editorStore.viewMode}
              versions={editorStore.versions}
              isOpen={editorStore.activeSidebar === SidebarEnum.Versioning}
              onClose={editorStore.closeSidebar}
              onRevert={handleRevertVersion}
              hasUnpublishedChanges={editorStore.hasUnpublishedChanges}
            />
          </FileUploadProvider>
        </EditorContext.Provider>
      </Box>
    );
  },
);

export const SimpleEditor = (props: SimpleEditorProps): React.ReactNode => (
  <TocProvider>
    <SimpleEditorContent {...props} />
  </TocProvider>
);

export { EditorLoadingStates, EditorToolbar, EditorContentWrapper, EditorSidebar, EditorModals };
